/**
 * @lodex-pro/web-portal/routes/TicketDetail — read-only ticket view.
 *
 * Phase 15 / Task 15.4 (DEC-153) built this as an in-browser view only. Operator Reports (a)
 * (migration 0241) adds "Download PDF": the run ticket the hauler's load emails attach,
 * rendered by the API (components/DownloadTicketPdf). The PDF is the paper ticket, so unlike
 * this page it carries the driver's name and signature; never the driver's notes.
 *
 * Fields mirror the PRINTED run ticket (`@lodex-pro/shared/printing/run-ticket`), which is
 * the reference for which fields appear and in what order, so what an operator reads here
 * and what the driver hands them on paper cannot disagree. `formatGauge` and `formatBbl` are
 * imported from the ESC/POS helpers for exactly that reason — one formatter, two surfaces.
 *
 * Deliberately NOT shown, decided with Corbin before building: driver name, truck number and
 * rated capacity, driver notes, and service charges. All appear on the paper the driver hands
 * over, but stay out of the portal as hauler-internal, personal, or billing data.
 */

import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useBackTo } from '../lib/url-state';
import { formatGauge, formatBbl } from '@lodex-pro/shared/printing/escpos-commands';
import type { PortalTicketTankReading } from '@lodex-pro/shared/validation/operator-portal-tickets';
import { usePortalTicket } from '../hooks/useOperatorPortal';
import { PortalDataError } from '../components/PortalDataError';
import { DownloadTicketPdf } from '../components/DownloadTicketPdf';

const DASH = '—';

/**
 * Local date + time with an explicit 12-hour clock.
 *
 * DEC-004: UTC is never shown to a user. DEC-066: date-bounded values render local, 12-hour
 * AM/PM. `hour12` is passed explicitly rather than inherited from the locale, so a viewer
 * whose locale defaults to a 24-hour clock still sees the same shape as the printed ticket.
 */
function formatLocalDateTime(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/** Gauge via the printed ticket's own formatter; `null` columns become `undefined` for it. */
function gauge(feet: number | null, inches: number | null): string {
  if (feet === null && inches === null) return DASH;
  return formatGauge(feet ?? undefined, inches ?? undefined);
}

/** BBL via the printed ticket's own formatter. netBbl is numeric-as-string from Postgres. */
function bbl(value: string | null): string {
  if (value === null || value === '') return DASH;
  const n = Number(value);
  if (Number.isNaN(n)) return DASH;
  return formatBbl(n);
}

const TH: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', whiteSpace: 'nowrap' };
const TH_R: React.CSSProperties = { ...TH, textAlign: 'right' };
const TD: React.CSSProperties = { padding: '8px 12px' };
const TD_R: React.CSSProperties = { ...TD, textAlign: 'right' };
const DT: React.CSSProperties = { color: 'var(--color-text-tertiary)' };
const DD: React.CSSProperties = { margin: 0 };

export function TicketDetail(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const ticketId = id ?? '';
  // OPEN-191 — back to the list as left (its page and dates), under either portal mount.
  const backTo = useBackTo('/portal/tickets', { allowed: ['/portal/operator/tickets'] });
  const { data, isLoading, isError, error } = usePortalTicket(ticketId);

  const tanks: readonly PortalTicketTankReading[] = data?.tanks ?? [];

  // The printed ticket swaps TOP/BOTTOM gauge lines for OPEN/CLOSE depending on the capture
  // method rather than printing empty rows for whichever is absent. Same idea here: a
  // SINGLE_GAUGE capture has no before/after reading at all (null on every such row), so
  // showing those two columns would be two dashes per tank on most real tickets.
  const hasGauges = tanks.some((t) => t.topGaugeFeet !== null || t.bottomGaugeFeet !== null);
  const hasBeforeAfter = tanks.some((t) => t.beforeBbl !== null || t.afterBbl !== null);
  const hasProduct = tanks.some((t) => t.productName !== null);
  const hasSeals = tanks.some((t) => t.sealOnNumber !== null || t.sealOffNumber !== null);

  // Tank + Color cut + Net BBL are always present; the rest are conditional.
  const columnCount =
    3 + (hasProduct ? 1 : 0) + (hasGauges ? 2 : 0) + (hasBeforeAfter ? 2 : 0) + (hasSeals ? 2 : 0);

  return (
    <main style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ marginBottom: '12px' }}>
        <Link to={backTo} style={{ color: 'var(--color-link)', fontSize: '13px' }}>
          ← Back to tickets
        </Link>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        <h1 style={{ margin: 0 }}>Ticket {data?.haulId ?? ticketId}</h1>
        {data !== undefined && <DownloadTicketPdf haulId={data.haulId} />}
      </div>

      {isLoading && <p role="status">Loading ticket…</p>}
      {isError && <PortalDataError error={error} fallback="Could not load ticket." />}

      {data !== undefined && (
        <>
          <section aria-label="Capture info" style={{ marginBottom: '24px' }}>
            <h2 style={{ fontSize: '15px', marginBottom: '8px' }}>Capture</h2>
            <dl style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '6px 12px' }}>
              <dt style={DT}>Job type</dt>
              <dd style={DD}>{data.jobType === '' ? DASH : data.jobType}</dd>
              <dt style={DT}>Captured at</dt>
              <dd style={DD}>{formatLocalDateTime(data.capturedAt)}</dd>
              <dt style={DT}>Pickup</dt>
              {/* null is a real state, not an error — a device may never have recorded the
                  arrival at the well. Say so rather than showing a blank or a fake date. */}
              <dd style={DD}>
                {data.pickupAt === null ? 'Not recorded' : formatLocalDateTime(data.pickupAt)}
              </dd>
              <dt style={DT}>Drop-off</dt>
              {/* Likewise: the haul may still be in flight, or the job type may complete at
                  the gauge (ON_SITE) and have no disposal leg at all. */}
              <dd style={DD}>
                {data.dropoffAt === null
                  ? 'Not yet delivered'
                  : formatLocalDateTime(data.dropoffAt)}
              </dd>
              <dt style={DT}>Site</dt>
              <dd style={DD}>{data.siteName ?? DASH}</dd>
              <dt style={DT}>Hauler</dt>
              <dd style={DD}>{data.haulerName === '' ? DASH : data.haulerName}</dd>
              <dt style={DT}>Disposal destination</dt>
              <dd style={DD}>{data.deliveredToDisposalFacilityName ?? DASH}</dd>
              <dt style={DT}>Total BBL</dt>
              <dd style={{ ...DD, fontWeight: 600 }}>{bbl(data.totalBbl)}</dd>
              {/* DEC-302 — when a Truck Pusher corrected the volume in review (DEC-125),
                  Total BBL above is the CORRECTED figure, which is what the invoice
                  carries. The gauged figure is shown too rather than replaced: the tank
                  readings below still add up to it, and an operator reconciling gauges
                  against an invoice needs both numbers and the fact that they differ.
                  Absent entirely on an uncorrected ticket, which is the normal case. */}
              {data.reviewedTotalBbl !== null && (
                <>
                  <dt style={DT}>Corrected in review</dt>
                  <dd style={DD}>
                    Gauged {bbl(data.capturedTotalBbl)} BBL, corrected to{' '}
                    {bbl(data.reviewedTotalBbl)} BBL
                  </dd>
                </>
              )}
            </dl>
          </section>

          {tanks.length > 0 && (
            <section aria-label="Tank readings">
              <h2 style={{ fontSize: '15px', marginBottom: '8px' }}>
                Tank readings{tanks.length > 1 ? ` (${String(tanks.length)} tanks)` : ''}
              </h2>
              {/* Wide on multi-tank tickets with gauges AND before/after; scroll the table
                  rather than letting the page scroll sideways. */}
              <div style={{ overflowX: 'auto' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    background: 'var(--color-bg-secondary)',
                    border: '1px solid var(--color-border-primary)',
                  }}
                >
                  <thead>
                    <tr style={{ background: 'var(--color-bg-tertiary)' }}>
                      <th style={TH}>Tank</th>
                      {hasProduct && <th style={TH}>Product</th>}
                      {hasGauges && <th style={TH_R}>Top gauge</th>}
                      {hasGauges && <th style={TH_R}>Bottom gauge</th>}
                      {hasBeforeAfter && <th style={TH_R}>Before (BBL)</th>}
                      {hasBeforeAfter && <th style={TH_R}>After (BBL)</th>}
                      {hasSeals && <th style={TH}>Seal on</th>}
                      {hasSeals && <th style={TH}>Seal off</th>}
                      <th style={TH}>Color cut</th>
                      <th style={TH_R}>Net BBL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tanks.map((r) => (
                      <tr
                        key={r.position}
                        style={{ borderTop: '1px solid var(--color-border-primary)' }}
                      >
                        <td style={TD}>{r.tankName}</td>
                        {hasProduct && <td style={TD}>{r.productName ?? DASH}</td>}
                        {hasGauges && (
                          <td style={TD_R}>{gauge(r.topGaugeFeet, r.topGaugeInches)}</td>
                        )}
                        {hasGauges && (
                          <td style={TD_R}>{gauge(r.bottomGaugeFeet, r.bottomGaugeInches)}</td>
                        )}
                        {hasBeforeAfter && <td style={TD_R}>{bbl(r.beforeBbl)}</td>}
                        {hasBeforeAfter && <td style={TD_R}>{bbl(r.afterBbl)}</td>}
                        {hasSeals && <td style={TD}>{r.sealOnNumber ?? DASH}</td>}
                        {hasSeals && <td style={TD}>{r.sealOffNumber ?? DASH}</td>}
                        <td style={TD}>{r.colorCut ?? DASH}</td>
                        <td style={TD_R}>{bbl(r.netBbl)}</td>
                      </tr>
                    ))}
                  </tbody>
                  {tanks.length > 1 && (
                    <tfoot>
                      <tr
                        style={{
                          borderTop: '2px solid var(--color-border-primary)',
                          fontWeight: 600,
                        }}
                      >
                        {/* Label spans every column except the last; the total sits under
                            the Net BBL column it is the sum of. */}
                        <td style={TD} colSpan={columnCount - 1}>
                          Total
                        </td>
                        {/* capturedTotalBbl, NOT totalBbl (DEC-302). A review correction
                            is recorded on the capture and the captured tank values are
                            never destroyed (DEC-125), so this column is the one that sums
                            to the rows above it. Showing the corrected figure here would
                            be a footer that does not add up. */}
                        <td style={TD_R}>{bbl(data.capturedTotalBbl)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </main>
  );
}
